import { captureError, captureMessage } from 'conversations/utils/logRocket'
import { Epic } from 'redux-observable'
import { empty, from, of } from 'rxjs'
import { bufferTime, catchError, filter, map, mergeMap, pluck, switchMap } from 'rxjs/operators'
import { track } from 'shared/store/actions/tracking'
import { patchNotificationSettingsBySubscription } from 'shared/store/actions/user'
import {
  createConvoSidebarSectionAsync,
  deleteConvoSidebarSectionAsync,
  fetchSectionsAsync,
  sharedSectionChangedNotification,
  updateConvoSidebarSectionAsync,
} from 'shared/store/actions/user/sections'
import {
  ConversationSidebarSectionConfig,
  SharedSectionChangeConfig,
} from 'shared/typings/conversationsSidebar/Filters'

import { access, GlobalStore as store } from '@driftt/traffic-control'

import { isActionOf, RootAction, RootState, Services } from 'typesafe-actions'

import * as api from './api'

export const handleCreateConvoSidebarSectionV2: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(createConvoSidebarSectionAsync.request)),
    pluck('payload'),
    switchMap((payload) => {
      const data = payload.config
      store.dispatch(
        track({
          name: 'CreatedConvoSectionV2',
          properties: {
            sectionName: data.title,
            shared: data.shared,
          },
        })
      )
      return from(api.createSidebarSection(data)).pipe(
        map((section) => createConvoSidebarSectionAsync.success({ config: section })),
        catchError((error: Error) => {
          captureError(error, 'Create convo sidebar section failed')
          return of(createConvoSidebarSectionAsync.failure(error))
        })
      )
    })
  )

export const handleUpdateConvoSidebarSectionsV2: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(updateConvoSidebarSectionAsync.request)),
    pluck('payload'),
    mergeMap((payload) => {
      store.dispatch(
        track({
          name: 'UpdatedConvoSectionsV2',
          properties: { id: payload.config.id, shared: payload.config.shared },
        })
      )
      return from(api.updateSidebarSection(payload.config)).pipe(
        map((updatedSection) => {
          return updateConvoSidebarSectionAsync.success({
            config: updatedSection,
            hideToast: payload.hideToast,
          })
        }),
        catchError((error: Error) => {
          captureError(error, 'Update convo sidebar section failed')
          return of(updateConvoSidebarSectionAsync.failure(error))
        })
      )
    })
  )

export const handleDeleteConvoSidebarSectionV2: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(deleteConvoSidebarSectionAsync.request)),
    pluck('payload'),
    mergeMap((payload) => {
      store.dispatch(
        track({
          name: 'DeletedConvoSection',
          properties: { sectionTitle: payload.title, id: payload.id, shared: payload.shared },
        })
      )
      return from(api.deleteSidebarSection(payload)).pipe(
        map(() => {
          return deleteConvoSidebarSectionAsync.success(payload)
        }),
        catchError((error: Error) => {
          captureError(error, 'Delete convo sidebar section failed')
          return of(deleteConvoSidebarSectionAsync.failure(error))
        })
      )
    })
  )

export const patchNotificationSettingsBySubscriptionEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) =>
  action$.pipe(
    filter(isActionOf(patchNotificationSettingsBySubscription)),
    pluck('payload'),
    switchMap((payload) => {
      from(api.patchNotificationsBySection(payload)).pipe(
        catchError((error) => {
          captureError(error, 'Patch notification by section failed')
          return empty()
        })
      )
      return empty()
    })
  )

export const waitFetchSections: Epic<RootAction, RootAction, RootState, Services> = (action$) =>
  action$.pipe(
    filter((action) => action.type === 'RECEIVE_USER_CONFIG'),
    map(() => fetchSectionsAsync.request(undefined))
  )

export const fetchSectionsV2: Epic<RootAction, RootAction, RootState, Services> = (action$) => {
  return action$.pipe(
    filter(isActionOf(fetchSectionsAsync.request)),
    pluck('payload'),
    bufferTime(1000),
    filter((buffer) => buffer.length > 0),
    switchMap((prevSections) =>
      from(api.fetchSections()).pipe(
        mergeMap((sections) => {
          const hasSharedSections = access.hasFeatures([
            { featureName: 'conversations_shared_sections', accessLevel: 'READ' },
          ])
          const sectionsToStore = hasSharedSections
            ? sections
            : sections.filter((s: ConversationSidebarSectionConfig) => !s.shared)
          if (sectionsToStore.some((s) => !s.condition && !s.subscriptionId)) {
            captureMessage('User has section with null condition and subscription.')
          }
          const filterOutNullSubscriptions = sectionsToStore.filter(
            (s) => s.condition && s.subscriptionId
          )
          //remove undefined payloads
          const removeUndefined = (
            item: SharedSectionChangeConfig | undefined
          ): item is SharedSectionChangeConfig => {
            return !!item
          }
          const prevSectionsWithoutUndefined = prevSections.filter(removeUndefined)

          //deduplicate by subscriptionId
          //use map to get the last occurance instead of the first
          const dedupe = (
            arr: SharedSectionChangeConfig[],
            map = new Map()
          ): SharedSectionChangeConfig[] => {
            arr.forEach((o) => map.set(o.subscriptionId, o))
            return [...map.values()]
          }
          const dedupedPrevSections = dedupe(prevSectionsWithoutUndefined)

          if (dedupedPrevSections.length > 0) {
            return of(
              fetchSectionsAsync.success(filterOutNullSubscriptions),
              sharedSectionChangedNotification({
                updatedSections: filterOutNullSubscriptions,
                prevSections: dedupedPrevSections,
              })
            )
          } else {
            return of(fetchSectionsAsync.success(filterOutNullSubscriptions))
          }
        }),
        catchError((error: Error) => {
          //not necessary long term to have this, but need it temporarily to test a theory - @cmcgorrill
          captureError(error, 'fetchSectionsAsync failed')
          return of(fetchSectionsAsync.failure(error))
        })
      )
    )
  )
}
